import React from 'react';
import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import MuiTypography from '@mui/material/Typography';
import Chart from 'react-apexcharts';
import { traducciones } from '../../views/pages/operator/ProductionRecordsPage';

/**
 * Presentational chart: Yield por hora
 *
 * Props:
 *   data — YieldByHourDTO object from POST /api/stats/yield-by-hour:
 *     { categories, goodCountSeries, defectiveSeries, yieldSeries }
 *
 * Reusable: accepts data as prop, has no internal fetch logic.
 */
const YieldByHourChart = ({ data, height = 350, showLabel = true, isDarkMode = false, showToolbar = true }) => {
    const theme = useTheme();
    const GREEN = theme.palette.primary.main;
    const fontFamily = `Inter, sans-serif`;
    const LINE_COLOR = theme.palette.secondary.dark;
    const AXIS_COLOR = isDarkMode ? '#FFFFFF' : theme.palette.grey[700];
    if (!data) return null;
    const { categories = [], goodCountSeries = [], defectiveSeries = [], yieldSeries = [] } = data || {};

    // yieldSeries values are 0.0–1.0; null means no production that hour → normalize to 0
    const yieldPercent = yieldSeries.map((v) => (v !== null && v !== undefined ? +(v * 100).toFixed(1) : null));

    const hasValidData = yieldPercent.some((val) => val !== null && val !== undefined);

    const chartOptions = {
        chart: {
            type: 'line',
            toolbar: {
                show: showToolbar,
                export: {
                    csv: {
                        filename: `Gráfico de Yield (%).`,
                        headerCategory: 'Horas'
                    },
                    svg: { filename: `Gráfico de Yield (%).` },
                    png: { filename: `Gráfico de Yield (%).` }
                }
            },
            fontFamily,
            height: height,
            locales: [traducciones],
            defaultLocale: 'es',
            animations: {
                enabled: false
            }
        },
        noData: {
            text: 'No hay datos disponibles',
            align: 'center',
            verticalAlign: 'middle',
            style: {
                color: AXIS_COLOR,
                fontSize: '16px',
                fontFamily: fontFamily
            }
        },
        plotOptions: {
            bar: {
                columnWidth: '70%',
                borderRadius: 0
            }
        },
        stroke: {
            curve: 'smooth',
            width: 3
        },
        dataLabels: {
            enabled: true,
            enabledOnSeries: [0],
            formatter: (val) => {
                if (val === null || val === undefined || val === 0) return '';
                return val;
            },
            style: {
                fontSize: '16px',
                fontWeight: 'bold',
                fontFamily
            },
            background: {
                enabled: true,
                borderRadius: 4,
                padding: 4,
                opacity: 0.9,
                background: LINE_COLOR
            }
        },
        xaxis: {
            categories: categories,
            tickAmount: 8,
            labels: {
                style: {
                    colors: AXIS_COLOR,
                    fontSize: '16px'
                }
            },

            axisBorder: {
                show: true,
                color: AXIS_COLOR
            },
            axisTicks: {
                show: true,
                color: AXIS_COLOR
            }
        },
        yaxis: [
            {
                seriesName: 'Yield %',
                min: 0,
                max: 100,
                title: { text: 'Yield %', style: { color: AXIS_COLOR, fontSize: '16px' } },
                labels: {
                    style: { colors: AXIS_COLOR, fontSize: '16px' },
                    formatter: (v) => `${v}%`
                }
            }
        ],
        legend: {
            position: 'bottom',
            fontSize: '16px',
            labels: {
                colors: AXIS_COLOR,
                fontFamily
            }
        },
        grid: {
            borderColor: '#eeeeee',
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } }
        },
        tooltip: {
            theme: 'dark',
            shared: true,
            intersect: false,
            y: [
                {
                    formatter: (v) => {
                        if (v === null || v === undefined) return 'Sin producción';
                        else {
                            return v;
                        }
                    }
                }
            ]
        },
        colors: [LINE_COLOR]
    };

    // const chartSeries = [
    //     //{ name: 'Piezas buenas', type: 'bar', data: goodCountSeries },
    //     // { name: 'Defectos', type: 'bar', data: defectiveSeries },
    //     { name: 'Yield %', type: 'line', data: yieldPercent }
    // ];

    const chartSeries = hasValidData ? [{ name: 'Yield %', type: 'line', data: yieldPercent }] : [];

    // return <ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={320} />;
    return (
        <Box>
            {showLabel && (
                <MuiTypography variant="h5" sx={{ fontWeight: 400, textAlign: 'center', color: 'gray' }}>
                    Yield por hora.
                </MuiTypography>
            )}

            <Chart options={chartOptions} series={chartSeries} height={height} type="line" />
        </Box>
    );
};

YieldByHourChart.propTypes = {
    data: PropTypes.shape({
        categories: PropTypes.arrayOf(PropTypes.string),
        goodCountSeries: PropTypes.arrayOf(PropTypes.number),
        defectiveSeries: PropTypes.arrayOf(PropTypes.number),
        yieldSeries: PropTypes.arrayOf(PropTypes.number)
    })
};

export default YieldByHourChart;
